// src/publish/pieces/generic.js — fallback pieces for any shape no builder owns.
//
// Covers two cases the dispatch cannot name: a `content` that is a plain string
// (legacy entries, re-imported runs) and an object with the base fields every
// format shares (title, hook, body, cta, hashtags, cta_url, full_text) but an
// unknown platform / content_format. Pieces, in order, only when non-empty:
//   title, hook, body (primary), cta, hashtags ('tags'), link ('url')
// When neither body nor hook exists but full_text does, one 'content' piece
// carries full_text; a string content gives the same single 'content' piece.
// The primary is body, else hook, else content, else the first piece — exactly one.
// i18n format key: 'generic' (outputs:pieces.destinations.generic.<id>).

import { normalizeText } from '../normalize.js';
import { makePiece, ctaUrlOf, tagsBlocks, urlBlocks, ensureOnePrimary, str } from '../piece.js';

const FORMAT = 'generic';

export function build(entry) {
  const content = entry?.results?.content;
  const pieces = [];

  if (typeof content === 'string') {
    const blocks = normalizeText(content, { headings: true });
    if (blocks.length) pieces.push(makePiece(FORMAT, { id: 'content', kind: 'rich', blocks }));
  } else if (content && typeof content === 'object') {
    const title = normalizeText(str(content.title));
    const hook = normalizeText(str(content.hook));
    const body = normalizeText(str(content.body), { headings: true });
    const cta = normalizeText(str(content.cta));
    if (title.length) pieces.push(makePiece(FORMAT, { id: 'title', blocks: title }));
    if (hook.length) pieces.push(makePiece(FORMAT, { id: 'hook', blocks: hook }));
    if (body.length) pieces.push(makePiece(FORMAT, { id: 'body', kind: 'rich', blocks: body }));
    if (cta.length) pieces.push(makePiece(FORMAT, { id: 'cta', blocks: cta }));
    if (!body.length && !hook.length) {
      const full = normalizeText(str(content.full_text), { headings: true });
      if (full.length) pieces.push(makePiece(FORMAT, { id: 'content', kind: 'rich', blocks: full }));
    }
    const tags = tagsBlocks(content.hashtags);
    if (tags.length) pieces.push(makePiece(FORMAT, { id: 'hashtags', kind: 'tags', blocks: tags }));
    const url = urlBlocks(ctaUrlOf(entry));
    if (url.length) pieces.push(makePiece(FORMAT, { id: 'link', kind: 'url', blocks: url }));
  }

  return ensureOnePrimary(pieces, ['body', 'hook', 'content']);
}
